<template>
  <div class="composite-team-head collapsed" ref="element">
    <!-- Where the heads are displayed -->
    <HeadCanvas v-if="headData" :expanded="isExpanded" :model="headData"></HeadCanvas>
    <!-- The controls for the display -->
    <HeadToolbar
      :expanded="isExpanded"
      :model="headData"
      @expand="onExpand"
      @collapse="onCollapse"
    ></HeadToolbar>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { debug as d } from "debug";
import { TweenMax } from "gsap";
import { useAxios } from "../../composables/api";
import { CompositeHeadApiModel } from "../../api-models";
import HeadCanvas from "./3d/head-canvas.vue";
import HeadToolbar from "./3d/head-toolbar.vue";
import { SoloOrViewpointsOption } from "./3d/nodes-and-links-models";
import { transitionMilliseconds } from "./3d/use-nodes-and-links-graph";

const debug = d("cs.CompositeTeamHead");

const props = defineProps<{
  teamId: string;
  initialSoloOrViewpoints: SoloOrViewpointsOption;
}>();
const element = ref<HTMLElement>();
const isLoading = ref<boolean>(true);
const isExpanded = ref<boolean>(false);

const sharedCode = new URL(window.location.href).searchParams.get("sharedcode");
const url =
  sharedCode == null
    ? `/api/head/composite/team/${props.teamId}`
    : `/api/head/composite/team/${props.teamId}?sharedCode=${sharedCode}`;

const { data: headData, promise } = useAxios<CompositeHeadApiModel>(url);

promise.then(() => {
  isLoading.value = false;
});

function onExpand() {
  isExpanded.value = true;
  element.value!.classList.add("expanded");
  element.value!.classList.remove("collapsed");
}

function onCollapse() {
  isExpanded.value = false;
  // Delay resize to allow animation to happen
  setTimeout(() => {
    element.value!.classList.add("collapsed");
    element.value!.classList.remove("expanded");
  }, transitionMilliseconds);
}
</script>

<style lang="less" scoped>
@import (reference) "../../styles/bootstrap/variables";

.composite-team-head {
  width: 100%;
  aspect-ratio: 1.8 / 1;
  position: relative;
  overflow: hidden;
  background-color: white;

  &.expanded {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: @zindex-fullscreen-overlay;
  }
}
</style>
